import { useEffect } from "react";
import { company as companyDefault } from "@/config/site";
import { useSettings } from "@/context/SettingsContext";

// Lightweight SEO manager: sets document title, meta description, canonical, OG & Twitter tags.
// CMS-managed values (settings.seo[path]) take precedence over the props passed in by pages.
export default function SEO({
  title,
  description,
  path = "",
  image = "",
  type = "website",
  jsonLd = null,
}) {
  const { company, seo } = useSettings();
  const brand = company || companyDefault;
  const override = (seo && seo[path]) || {};
  const finalTitle = override.title || title;
  const finalDescription = override.description || description;

  useEffect(() => {
    const fullTitle = finalTitle || `${brand.name}`;
    document.title = fullTitle;

    const setMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const domain = (brand.domain || "").replace(/^https?:\/\//, "");
    const canonicalUrl = `https://${domain}${path}`;

    setMeta("name", "description", finalDescription);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", finalDescription);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", canonicalUrl);
    if (image) setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", finalDescription);
    if (image) setMeta("name", "twitter:image", image);

    // Canonical
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);

    // JSON-LD structured data
    let script = document.head.querySelector('script[data-seo-jsonld="true"]');
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-jsonld", "true");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [finalTitle, finalDescription, path, image, type, jsonLd, brand.name, brand.domain]);

  return null;
}
